import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, CheckCircle2, GraduationCap, BarChart3, Headphones, Mic, TrendingUp, BookOpen, Lock } from "lucide-react";
import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, type CarouselApi } from "@/components/ui/carousel";
import { supabase } from "@/integrations/supabase/client";
import Autoplay from "embla-carousel-autoplay";
import introVideo from "@/assets/intro-sequence.mp4.asset.json";

const Index = () => {
  const { t, language } = useLanguage();
  const fr = language === "fr";
  const [updates, setUpdates] = useState<any[]>([]);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  useEffect(() => {
    const fetchUpdates = async () => {
      const { data } = await supabase
        .from("latest_updates")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true })
        .limit(5);
      
      setUpdates(data || []);
    };

    fetchUpdates();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-hero noise">
        {/* Soft radial accents */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-accent/8 blur-[100px] animate-float-slow" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-primary-foreground/8 blur-[100px] animate-float-diagonal" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-accent/4 blur-[80px] animate-float" />
        </div>
        
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }} />
        
        <div className="container mx-auto px-4 py-20 md:py-28 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-extrabold mb-6 text-primary-foreground uppercase tracking-tighter leading-[0.85] whitespace-nowrap">
              {t("home.title")}
            </h1>
            <p className="text-xl md:text-2xl mb-14 font-handwritten text-primary-foreground/75">
              {t("home.subtitle")}
            </p>
            <Link to="/resources" className="no-link-style">
              <Button size="lg" variant="hero" className="text-base px-10 py-5 h-auto rounded-full">
                {t("home.startTraining")}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Bottom curve */}
        <div className="absolute -bottom-px left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none">
            <path d="M0 60V30C360 0 720 0 1080 30C1260 45 1360 50 1440 45V60H0Z" fill="hsl(var(--background))" />
          </svg>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-14 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-[minmax(0,1fr)_1.5fr] gap-8 md:gap-12 items-center">
            <div className="relative mx-auto w-full max-w-[240px] md:max-w-[280px]">
              <div className="relative rounded-2xl overflow-hidden shadow-card aspect-[4/5] bg-primary/5">
                <video
                  src={introVideo.url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Teal tinge */}
                <div className="absolute inset-0 bg-primary/25 mix-blend-multiply pointer-events-none" />
                {/* Diagonal stripes */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-40"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, hsl(var(--primary) / 0.35) 0px, hsl(var(--primary) / 0.35) 1px, transparent 1px, transparent 7px)",
                  }}
                />
              </div>
            </div>
            <div className="text-left">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-foreground tracking-tight">
                {t("index.welcomeTitle")}
              </h2>
              <div className="space-y-3 text-sm md:text-base leading-relaxed text-muted-foreground">
                <p>{t("index.welcomeIntro1")}</p>
                <p>{t("index.welcomeIntro2")}</p>
                <p className="text-2xl md:text-3xl font-handwritten text-primary mt-4">
                  {t("index.welcomeIntro3")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Updates Section */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">{t("indexExt.stayInformed")}</p>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                {t("index.latestUpdates")}
              </h2>
            </div>
            {updates.length > 0 ? (
              <Carousel 
                className="w-full"
                setApi={setCarouselApi}
                plugins={[
                  Autoplay({
                    delay: 5000,
                    stopOnInteraction: false,
                  }),
                ]}
                opts={{
                  loop: true,
                }}
              >
                <CarouselContent>
                  {updates.map((update) => (
                    <CarouselItem key={update.id}>
                      <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-card">
                        <p className="text-xs text-accent font-bold uppercase tracking-widest mb-2">{update.date_label}</p>
                        <h3 className="text-lg md:text-xl font-bold mb-2 text-foreground tracking-tight">{update.title}</h3>
                        <p className="text-muted-foreground text-sm md:text-base mb-4 leading-relaxed">
                          {update.description}
                        </p>
                        <Link to={update.link_url} className="text-primary hover:text-primary/80 font-semibold inline-flex items-center gap-2 group text-sm">
                          {update.link_text} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="flex justify-center items-center gap-3 mt-6">
                  <CarouselPrevious className="relative left-0 top-0 translate-y-0 bg-card text-foreground hover:bg-muted border border-border shadow-card h-9 w-9" />
                  <CarouselNext className="relative right-0 top-0 translate-y-0 bg-card text-foreground hover:bg-muted border border-border shadow-card h-9 w-9" />
                </div>
              </Carousel>
            ) : (
              <p className="text-center text-muted-foreground">{t("index.noUpdates")}</p>
            )}
          </div>
        </div>
      </section>

      {/* Inside Le Coin Étude */}
      <section className="py-14 md:py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">
                {fr ? "Derrière la connexion" : "Behind the login"}
              </p>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">
                {fr ? "Ce qui t'attend dans Le Coin Étude" : "What you'll find inside Le Coin Étude"}
              </h2>
              <p className="text-muted-foreground text-sm max-w-xl mx-auto">
                {fr
                  ? "Un aperçu de la plateforme accessible dès ton inscription."
                  : "A glimpse of the platform you unlock the moment you enrol."}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: BookOpen, title: fr ? "Cours en autonomie" : "Self-paced courses", desc: fr ? "Apprends à ton rythme entre les sessions." : "Learn at your own pace between coaching sessions." },
                { icon: Headphones, title: fr ? "Enregistrements de cours" : "Lesson recordings", desc: fr ? "Revivre chaque séance quand tu veux." : "Revisit every coaching session whenever you need." },
                { icon: Mic, title: "Mystery Calls", desc: fr ? "Gagne en confiance à l'oral avec une pratique régulière." : "Build speaking confidence with regular conversation practice." },
                { icon: TrendingUp, title: fr ? "Ta progression" : "Your progress", desc: fr ? "Suis ton parcours d'apprentissage en un seul endroit." : "Track your learning journey in one place." },
              ].map(({ icon: Icon, title, desc }, i) => (
                <div key={i} className="group relative bg-card rounded-2xl border border-border shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                  {/* Mock browser preview */}
                  <div className="relative h-24 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 border-b border-border overflow-hidden">
                    <div className="absolute top-2 left-3 flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-400/70" />
                      <span className="w-2 h-2 rounded-full bg-yellow-400/70" />
                      <span className="w-2 h-2 rounded-full bg-green-400/70" />
                    </div>
                    <div className="absolute inset-0 mt-6 mx-3 mb-2 rounded-md bg-card/60 backdrop-blur-md p-2 flex flex-col gap-1">
                      <div className="h-1.5 rounded-full bg-primary/25 w-2/3" />
                      <div className="h-1.5 rounded-full bg-muted-foreground/20 w-full" />
                      <div className="h-1.5 rounded-full bg-muted-foreground/20 w-5/6" />
                    </div>
                    <div className="absolute inset-0 backdrop-blur-[3px] bg-background/10" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="p-2 rounded-full bg-card/90 shadow-sm border border-border">
                        <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="p-1.5 rounded-lg bg-primary/8 group-hover:bg-primary/12 transition-colors">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="font-bold text-sm group-hover:text-primary transition-colors">{title}</h3>
                    </div>
                    <p className="text-muted-foreground text-[13px] leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">
                {fr ? "Ton parcours" : "Your journey"}
              </p>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                {fr ? "Comment ça marche" : "How it works"}
              </h2>
            </div>
            <ol className="relative border-l-2 border-dashed border-primary/30 ml-4 md:ml-6 space-y-5">
              {[
                fr ? "Rejoins Quirky French" : "Join Quirky French",
                fr ? "Accès immédiat à Le Coin Étude" : "Access Le Coin Étude immediately",
                fr ? "Coaching hebdomadaire + apprentissage personnalisé" : "Weekly coaching + personalised learning",
                fr ? "Pratique entre les leçons" : "Practise between lessons",
                fr ? "Parle français avec confiance" : "Speak French with confidence",
              ].map((step, i) => (
                <li key={i} className="pl-6 md:pl-8 relative">
                  <span className="absolute -left-[13px] top-0 flex items-center justify-center w-6 h-6 rounded-md bg-primary text-primary-foreground text-xs font-bold shadow-card">
                    {i + 1}
                  </span>
                  <p className="text-sm md:text-base font-medium text-foreground pt-0.5">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-subtle" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-2xl md:text-4xl font-extrabold mb-4 tracking-tight">
            {t("home.ctaTitle")}
          </h2>
          <p className="text-sm md:text-base mb-8 max-w-xl mx-auto text-muted-foreground leading-relaxed">
            {t("home.ctaDescription")}
          </p>
          <Link to="/auth" className="no-link-style">
            <Button size="lg" className="px-10 py-5 h-auto text-base rounded-full">
              {fr ? "Se connecter" : "Sign in"}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <div className="mt-6 max-w-md mx-auto text-sm text-muted-foreground space-y-1.5">
            <p><span className="font-semibold text-foreground">{fr ? "Déjà apprenant·e Quirky French ?" : "Already a Quirky French learner?"}</span> {fr ? "Connecte-toi pour continuer." : "Sign in to continue your learning."}</p>
            <p><span className="font-semibold text-foreground">{fr ? "Nouveau ici ?" : "New here?"}</span> {fr ? "Ton accès est créé lors de ton inscription." : "Your access is created as part of your enrolment."}</p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Index;
